import React, { useState, useEffect } from 'react'
import { Form, Button } from 'react-bootstrap'
import { sendOTP } from '../../services/transfer'
import styles from './TiinyTransfer.module.css'

const EmailStep = ({ files = [], onNext, onBack, error, setError }) => {
  const [data, setData] = useState({
    uploaderEmail: '',
    recipientEmails: ''
  })

  const [otpSent, setOtpSent] = useState(false)
  const [loadingOtp, setLoadingOtp] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [otp, setOtp] = useState('')
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    const savedEmail = localStorage.getItem('tt_email')
    const isVerified = localStorage.getItem('tt_verified') === 'true'

    if (token && savedEmail && isVerified) {
      setData((prev) => ({ ...prev, uploaderEmail: savedEmail }))
      setIsAuthenticated(true)
      setOtpSent(true)
    }
  }, [])

  // Basic email validation
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleSendOTP = async () => {
    if (!isValidEmail(data.uploaderEmail)) {
      alert('Please enter a valid email address for yourself.')
      return
    }

    setLoadingOtp(true)
    setError('')
    try {
      const response = await sendOTP(data.uploaderEmail)

      if (response.success) {
        localStorage.setItem('tt_email', data.uploaderEmail)
        localStorage.setItem('tt_verified', 'false')
      }
      setOtpSent(true)
      setIsAuthenticated(false)
    } catch (err) {
      console.error(err)
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to send verification code. Please try again.'
      setError(errorMsg)
    } finally {
      setLoadingOtp(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!otpSent) return

    // Split recipient emails by comma
    const recipients = data.recipientEmails
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean)

    if (!isValidEmail(data.uploaderEmail)) {
      alert('Please enter a valid email address for yourself.')
      return
    }

    if (recipients.length > 0 && !recipients.every(isValidEmail)) {
      alert('Please enter valid recipient email addresses.')
      return
    }

    if (!isAuthenticated && (!otp || otp.length < 4)) {
      alert('Please enter a valid 4-digit verification code.')
      return
    }

    setVerifying(true)
    try {
      await onNext({
        uploaderEmail: data.uploaderEmail,
        recipientEmails: recipients,
        otp,
        authenticated: isAuthenticated
      })
    } catch (err) {
      console.error(err)
    } finally {
      setVerifying(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('tt_email')
    localStorage.removeItem('tt_verified')
    setIsAuthenticated(false)
    setOtpSent(false)
    setOtp('')
  }

  return (
    <div className="text-center">
      <h3 className="mb-4">Email Details</h3>

      <div className="d-flex align-items-center justify-content-center mb-4 p-3 bg-light rounded">
        <div className="me-3" style={{ fontSize: '24px' }}>
          📄
        </div>
        <div className="text-start">
          <div className="font-weight-bold">{files.length === 1 ? files[0]?.name : `${files.length} files`}</div>
          <div className="text-muted small">
            {(files.reduce((acc, f) => acc + (f?.size || 0), 0) / 1024 / 1024).toFixed(2)} MB
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <Form onSubmit={handleSubmit} className="text-start">
        <Form.Group className="mb-3">
          <Form.Label>Email to (optional, comma separated)</Form.Label>
          <Form.Control
            type="text"
            placeholder="friend@example.com, colleague@work.com"
            value={data.recipientEmails}
            onChange={(e) => setData({ ...data, recipientEmails: e.target.value })}
            required={false}
            className="rounded-3 p-2"
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Your Email</Form.Label>
          {!otpSent ? (
            <div className="input-group">
              <Form.Control
                type="email"
                placeholder="you@example.com"
                value={data.uploaderEmail}
                onChange={(e) => setData({ ...data, uploaderEmail: e.target.value })}
                required
                className="rounded-start-3 p-2"
              />
              <Button
                variant="outline-primary"
                onClick={handleSendOTP}
                disabled={loadingOtp || !isValidEmail(data.uploaderEmail)}
                className="rounded-end-3"
              >
                {loadingOtp ? 'Sending...' : 'Send OTP'}
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-2 p-2 bg-light rounded border d-flex justify-content-between align-items-center">
                <span>{data.uploaderEmail}</span>
                <Button variant="link" size="sm" onClick={handleLogout} className="p-0 text-decoration-none">
                  Change
                </Button>
              </div>
              {!isAuthenticated && (
                <Form.Control
                  type="text"
                  placeholder="Enter 4-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  maxLength={4}
                  className="rounded-3 p-2 text-center fs-5"
                  autoFocus
                />
              )}
            </>
          )}
        </Form.Group>

        <div className="d-grid gap-2">
          <Button
            variant="primary"
            size="lg"
            type="submit"
            className="rounded-3"
            disabled={!otpSent || (!isAuthenticated && otp.length < 4) || verifying}
          >
            {verifying ? 'Verifying...' : 'Transfer'}
          </Button>
          <Button variant="link" className="text-muted" onClick={onBack}>
            Back
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default EmailStep
